// Server state. Polling intervals are tuned so the board feels live without
// hammering Odoo; writes never assume success — the server's echo is the truth.

import { useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type PictureBucket } from '@/api/client';
import { localDateKey } from '@/lib/pace';
import { ApiError } from '@/api/http';
import { dueByNow, paceWindow } from '@/lib/pace';
import { type FlowlineTopology, buildTopology, routeOrder } from '@/lib/topology';
import { useT } from '@/i18n/useT';
import type { Translations } from '@/i18n/strings';
import type {
  AlertPayload,
  AlertVM,
  IswLang,
  AndonVM,
  BufferLineVM,
  CheckVM,
  FlowlineVM,
  NeedsYouVM,
  PodVM,
  ScoreLineVM,
  ScoreVM,
  StationStateVM,
  WorkcentreVM,
} from '@/api/types';
import { type StationTone, stationTone } from '@/lib/stationTone';
import { useStore } from '@/store';

export const keys = {
  refdata: ['refdata'] as const,
  flowlines: ['refdata', 'flowlines'] as const,
  workcentres: ['refdata', 'workcentres'] as const,
  teams: ['refdata', 'teams'] as const,
  lossReasons: ['refdata', 'loss'] as const,
  defectCats: ['refdata', 'defectCats'] as const,
  queue: (wc: number | null, fl: number | null) => ['queue', wc, fl] as const,
  checks: (wo: number | null) => ['checks', wo] as const,
  podFailedChecks: (mo: number | null) => ['podFailedChecks', mo] as const,
  woLive: (wo: number | null) => ['woLive', wo] as const,
  journey: (mo: number | null) => ['journey', mo] as const,
  alerts: (scope: string) => ['alerts', scope] as const,
  completedOn: (wc: number | null, fl: number | null, day: string) => ['completedOn', wc, fl, day] as const,
  stationStates: ['stationStates'] as const,
  targets: (date: string) => ['targets', date] as const,
  doneToday: (wc: number | null, byHour: boolean) => ['doneToday', wc, byHour] as const,
  calendars: (ids: number[]) => ['calendars', ids.join(',')] as const,
  andons: (scope: string) => ['andons', scope] as const,
  todos: (uid: number | null) => ['todos', uid] as const,
  departments: ['refdata', 'departments'] as const,
  flowlineOperations: ['refdata', 'flowlineOperations'] as const,
  flowlineBuffers: ['flowlineBuffers'] as const,
  bufferLines: ['bufferLines'] as const,
  crewToday: ['crew', 'today'] as const,
  qualityStages: (teamId: number | null) => ['qualityStages', teamId] as const,
  alertMessages: (alertId: number | null) => ['alertMessages', alertId] as const,
  routeSample: (flowlineIds: number[]) => ['routeSample', flowlineIds.join(',')] as const,
  // Keyed on the station and its lines, not on the pods sampled: the route is
  // slow-moving, and a key that changed every time a pod left the queue would
  // re-read it every few minutes for an answer that had not moved.
  podRouting: (wc: number | null, flowlineIds: number[]) =>
    ['podRouting', wc, flowlineIds.join(',')] as const,
  iswRegister: ['isw', 'register'] as const,
  iswRecord: (id: string, lang: string) => ['isw', 'record', id, lang] as const,
  drawings: (mo: number | null, folder: string | null) => ['drawings', mo, folder] as const,
};

const MINUTE = 60_000;

export function useFlowlines() {
  return useQuery({ queryKey: keys.flowlines, queryFn: api.flowlines, staleTime: 30 * MINUTE });
}

export function useWorkcentres() {
  return useQuery({ queryKey: keys.workcentres, queryFn: api.workcentres, staleTime: 30 * MINUTE });
}

export function useLossReasons() {
  return useQuery({ queryKey: keys.lossReasons, queryFn: api.lossReasons, staleTime: 30 * MINUTE });
}

export function useTeams() {
  return useQuery({ queryKey: keys.teams, queryFn: api.teams, staleTime: 30 * MINUTE });
}

export function useDefectCategories() {
  return useQuery({ queryKey: keys.defectCats, queryFn: api.defectCategories, staleTime: 30 * MINUTE });
}

/** Queue depth per work centre, for the station picker. */
export function useWaitingCounts(flowlineId: number | null) {
  return useQuery({
    queryKey: ['waitingCounts', flowlineId],
    queryFn: () => api.waitingCounts(flowlineId),
    staleTime: 60_000,
  });
}

/**
 * Raise a quality or safety alert, then attach its photos.
 *
 * A team that requires before/after pictures gets every photo linked into the
 * alert's "before" bucket; any other team keeps the plain attachment it always
 * had. The alert is created first and kept whatever happens to the photos —
 * a raise that failed after Odoo had the record would leave a QA number the
 * operator was never told about. A photo that does not upload is counted and
 * said so in a toast; the alert is where it can be added.
 */
export function useRaiseAlert() {
  const qc = useQueryClient();
  const onError = useWriteError();
  const showToast = useStore((s) => s.showToast);
  const t = useT();
  return useMutation({
    mutationFn: async ({
      payload,
      photos = [],
      bucket = 'other',
    }: {
      payload: AlertPayload;
      /** Bare base64 JPEGs, in the order they were taken. */
      photos?: string[];
      bucket?: PictureBucket;
    }) => {
      const raised = await api.raiseAlert(payload);
      let photosFailed = 0;
      for (const [i, base64] of photos.entries()) {
        // The BFF pins a nameless attachment to the alert just created, but
        // the bucket link is a write on the alert itself and needs its id —
        // which the create just returned, so it is named either way.
        const name = photos.length > 1 ? `${raised.qa || 'alert'}-${i + 1}.jpg` : `${raised.qa || 'alert'}.jpg`;
        await api.attachAlertPhoto(name, base64, raised.id, bucket).catch(() => {
          photosFailed += 1;
        });
      }
      return { ...raised, photosFailed };
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
      if (r.photosFailed > 0) {
        showToast(t.raisedPhotosFailedT.replace('{qa}', r.qa || 'Alert').replace('{n}', String(r.photosFailed)));
      }
    },
    onError,
  });
}

/**
 * Whether the station the app is pointed at is stopped.
 *
 * Read only. This app used to open and close the downtime record itself; the
 * redesign drops that control, so a stopped station is something to show and
 * not something to change from here. The banner it feeds is the one state an
 * operator must see on arrival, so a failure to read it is not swallowed into
 * "not blocked".
 */
export function useStationBlocked() {
  const workcentreId = useStore((s) => s.workcentreId);
  const openLine = useQuery({
    queryKey: ['blockLine', workcentreId],
    queryFn: () => api.openDowntime(workcentreId!),
    enabled: workcentreId != null,
    refetchInterval: 60_000,
  });
  return { blocked: openLine.data != null, reason: openLine.data?.reason ?? null };
}

/** The station queue. Ordered by production_seq, so index is queue position. */
export function useQueue() {
  const wc = useStore((s) => s.workcentreId);
  const fl = useStore((s) => s.flowlineId);
  return useQuery({
    queryKey: keys.queue(wc, fl),
    queryFn: () => api.queue(wc!, fl),
    enabled: wc != null,
    refetchInterval: 15_000,
    staleTime: 10_000,
  });
}

/**
 * The pod finder, answered from where the operator is standing.
 *
 * Keyed on the work centre as well as the text: the same query is a different
 * question at a different station, so the cache must not serve one station's
 * answer to another.
 */
export function usePodSearch(query: string) {
  const q = query.trim();
  // Scoped to the station, because the answer differs by where you stand.
  const workcentreId = useStore((s) => s.workcentreId);
  return useQuery({
    queryKey: ['podSearch', q, workcentreId] as const,
    queryFn: () => api.searchPods(q, workcentreId),
    enabled: q.length >= 2,
    staleTime: 30_000,
  });
}

export function useChecks(woId: number | null) {
  return useQuery({
    queryKey: keys.checks(woId),
    queryFn: () => api.checks(woId!),
    enabled: woId != null,
    refetchInterval: 20_000,
  });
}

/**
 * The pod's failed checks across its route, read when the Sign Off Anyway
 * sheet is up: a fresh read each time it opens, since a check may have been
 * passed at the bench a moment ago.
 */
export function usePodFailedChecks(moId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: keys.podFailedChecks(moId),
    queryFn: () => api.podFailedChecks(moId!),
    enabled: enabled && moId != null,
    staleTime: 0,
  });
}

/** Live timing, anchored to the server rather than a client counter. */
export function useWoLive(woId: number | null) {
  return useQuery({
    queryKey: keys.woLive(woId),
    queryFn: () => api.woLive(woId!),
    enabled: woId != null,
    refetchInterval: 30_000,
  });
}

/**
 * Warm the job screen while the operator is still looking at the pod card.
 * Tapping Start should reveal a screen that is already populated, not one that
 * begins fetching.
 */
export function usePrefetchJob(woId: number | null, moId: number | null) {
  const qc = useQueryClient();
  useEffect(() => {
    if (woId == null) return;
    void qc.prefetchQuery({ queryKey: keys.checks(woId), queryFn: () => api.checks(woId), staleTime: 15_000 });
    void qc.prefetchQuery({ queryKey: keys.woLive(woId), queryFn: () => api.woLive(woId), staleTime: 15_000 });
    if (moId != null) {
      void qc.prefetchQuery({ queryKey: keys.journey(moId), queryFn: () => api.journey(moId), staleTime: MINUTE });
    }
  }, [woId, moId, qc]);
}

export function useJourney(moId: number | null) {
  return useQuery({
    queryKey: keys.journey(moId),
    queryFn: () => api.journey(moId!),
    enabled: moId != null,
    staleTime: MINUTE,
  });
}

/**
 * What this station finished on a named day.
 *
 * A day that is over is over: only today's list is polled, because re-asking
 * Odoo every minute what happened last Tuesday cannot return a different
 * answer, and the pods screen leaves the poll running while an operator reads
 * back through the week.
 */
export function useCompletedOn(day: string) {
  const wc = useStore((s) => s.workcentreId);
  const fl = useStore((s) => s.flowlineId);
  const live = day === localDateKey();
  return useQuery({
    queryKey: keys.completedOn(wc, fl, day),
    queryFn: () => api.completedOn(wc!, fl, day),
    enabled: wc != null,
    refetchInterval: live ? 60_000 : false,
    staleTime: live ? 0 : 5 * MINUTE,
  });
}

export function useCompletedToday() {
  return useCompletedOn(localDateKey());
}

// ── score, pace and station state ──────────────────────────────────────────

/**
 * Where every work centre stands on every flowline it serves.
 *
 * Read whole rather than scoped to one station, because the questions this
 * table answers are about the station AFTER this one: what is starved, what has
 * stopped, what has banked up between them. Filtering the read to the current
 * work centre made the downstream station unfindable, which quietly emptied the
 * starvation line and flattened the priority order to pace alone.
 *
 * One query for every screen, so the operator home, the board and the leader
 * board share a cache entry and a poll rather than each keeping their own.
 */
export function useStationStates() {
  return useQuery({
    queryKey: keys.stationStates,
    queryFn: () => api.stationStates(),
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/** A day's flowline plan and the per-station overrides of it — today by default. */
export function useTargets(date?: string) {
  const day = date ?? localDateKey();
  return useQuery({
    queryKey: keys.targets(day),
    queryFn: () => api.targets(day),
    // Targets are edited from the desk and the wallboards through the day, so
    // this is not reference data — but it moves in minutes, not seconds.
    refetchInterval: 5 * MINUTE,
    staleTime: 2 * MINUTE,
  });
}

export function useDoneToday(workcentreId?: number | null, byHour = false) {
  const wc = useStore((s) => s.workcentreId);
  const target = workcentreId === undefined ? wc : workcentreId;
  return useQuery({
    queryKey: keys.doneToday(target, byHour),
    queryFn: () => api.doneToday({ workcentreId: target, byHour }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/**
 * The shift calendars behind a set of work centres.
 *
 * Keyed on the sorted set rather than on one id, so the leader board asking for
 * three and the operator home asking for one share nothing they should not and
 * neither re-reads what the other has.
 */
export function useCalendars(calendarIds: Array<number | null | undefined>) {
  const ids = [...new Set(calendarIds.filter((v): v is number => typeof v === 'number' && v > 0))].sort(
    (a, b) => a - b,
  );
  return useQuery({
    queryKey: keys.calendars(ids),
    queryFn: () => api.calendars(ids),
    enabled: ids.length > 0,
    staleTime: 30 * MINUTE,
  });
}

/** One work centre's calendar, so pace is pro-rata against its own hours. */
export function useCalendar(calendarId: number | null) {
  const all = useCalendars([calendarId]);
  return { ...all, data: calendarId == null ? null : (all.data?.[calendarId] ?? null) };
}

/**
 * The route: who each station feeds, and how full the buffer between them is.
 *
 * Three sources, most authoritative first, falling back per pair rather than
 * per app — see `lib/topology.ts` for why none of them is work-centre
 * sequence. The pod sample is what makes the fallback work today: `mrp.flowline
 * .operation` and `mrp.flowline.buffer` are both empty on this instance, while
 * every live work order carries its dependencies.
 *
 * The station's own queue is sampled alongside the line-wide one because it is
 * already in cache and is, by definition, the variant the operator is holding.
 */
export function useFlowlineTopology(flowlineIds: number[], enabled = true): FlowlineTopology {
  const operations = useQuery({
    queryKey: keys.flowlineOperations,
    queryFn: api.flowlineOperations,
    enabled,
    staleTime: 30 * MINUTE,
  });
  const buffers = useQuery({
    queryKey: keys.flowlineBuffers,
    queryFn: api.flowlineBuffers,
    enabled,
    // Levels move as pods do; the edges they carry do not.
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const lines = [...flowlineIds].sort((a, b) => a - b);
  const sample = useQuery({
    queryKey: keys.routeSample(lines),
    queryFn: () => api.routeSampleMos(lines),
    enabled: lines.length > 0,
    staleTime: 10 * MINUTE,
  });

  // Pods at this station, from the queue already loaded. No extra read.
  const workcentreId = useStore((s) => s.workcentreId);
  const queue = useQueue();
  const here = (queue.data ?? []).map((p) => p.moId).filter((v): v is number => !!v);
  // Capped: a dozen pods is four pages of work orders at most, and one pod
  // already carries a whole line's route.
  const moIds = [...new Set([...here.slice(0, 6), ...(sample.data ?? [])])].slice(0, 12);

  const routing = useQuery({
    queryKey: keys.podRouting(workcentreId, lines),
    queryFn: () => api.podRouting(moIds),
    enabled: moIds.length > 0,
    staleTime: 10 * MINUTE,
  });

  // The buffer boards' own table, which is the one with rows on it today.
  const boardTable = useBufferLines();

  const ops = operations.data;
  const bufs = buffers.data;
  const edges = routing.data;
  const boardLines = boardTable.data;
  return useMemo(
    () => buildTopology(ops ?? [], bufs ?? [], edges ?? [], boardLines ?? []),
    [ops, bufs, edges, boardLines],
  );
}

/**
 * Every buffer on every flowline, as the kiosk buffer boards hold them.
 *
 * Read whole and shared: the leader's Buffers view, the "buffers feeding this
 * station" rail and the route topology all want the same fifty rows, and the
 * levels move as pods do.
 */
export function useBufferLines() {
  return useQuery({
    queryKey: keys.bufferLines,
    queryFn: api.bufferLines,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

/** The buffers that feed one work centre, on the flowlines in scope. */
export function useStationBuffers(workcentreId: number | null, flowlineIds: number[]): BufferLineVM[] {
  const lines = useBufferLines();
  return useMemo(
    () =>
      (lines.data ?? []).filter(
        (b) => workcentreId != null && b.outWorkcentreId === workcentreId && flowlineIds.includes(b.flowlineId),
      ),
    [lines.data, workcentreId, flowlineIds],
  );
}

/**
 * The station's score: done, target and pace, per flowline and rolled up.
 *
 * `target` resolves the way the floor plans it — the flowline's number for the
 * day, overridden per station only where somebody has deliberately set one.
 * `dueNow` is pro-rata against this station's own shift calendar, never the
 * wall clock, so the two-shift stations are not told they are losing at 9am.
 *
 * Everything is null rather than zero where Odoo has not been told: a station
 * with no target for today shows what it has done and says so, instead of
 * inventing a denominator to be behind.
 */
export function useScore(): ReturnType<typeof useScoreFor> {
  const wc = useStore((s) => s.workcentreId);
  const fl = useStore((s) => s.flowlineId);
  return useScoreFor(wc, fl);
}

/**
 * The same score for any work centre — the leader's headline is this, pointed
 * at End-of-Line QC, because a pod counts for the floor when it leaves it.
 *
 * @param fl One flowline, or null for every line the station runs.
 */
export function useScoreFor(
  wc: number | null,
  fl: number | null,
): {
  data: ScoreVM | null;
  isPending: boolean;
  states: StationStateVM[];
  workcentres: WorkcentreVM[];
  flowlines: FlowlineVM[];
} {
  const states = useStationStates();
  const targets = useTargets();
  const done = useDoneToday(wc);
  const workcentres = useWorkcentres();
  const flowlines = useFlowlines();

  const station = (workcentres.data ?? []).find((w) => w.id === wc) ?? null;
  const calendar = useCalendar(station?.calendarId ?? null);
  const window = paceWindow(calendar.data ?? null);

  const rows = (states.data ?? []).filter((r) => r.workcentreId === wc);
  const inScope = fl == null ? rows : rows.filter((r) => r.flowlineId === fl);

  const lines: ScoreLineVM[] = inScope
    // A pair with nothing routed to it on this line is not a flowline this
    // station is running today, whatever the plan says.
    .filter((r) => r.status !== 'no_work')
    .map((r) => {
      const override = targets.data?.workcentre[`${r.workcentreId}:${r.flowlineId}`];
      const inherited = targets.data?.flowline[r.flowlineId]?.target;
      const target = override ?? inherited ?? (r.targetToday || null);
      const dueNow = dueByNow(target, window);
      const count = (done.data ?? [])
        .filter((d) => d.workcentreId === r.workcentreId && d.flowlineId === r.flowlineId)
        .reduce((sum, d) => sum + d.count, 0);
      return {
        flowlineId: r.flowlineId,
        flowlineName: r.flowlineName,
        short: shortFlowline(r.flowlineName, flowlines.data ?? []),
        done: count,
        target: target ?? null,
        dueNow,
        delta: dueNow == null ? null : count - dueNow,
        status: r.status,
        outboundWaiting: r.outboundWaiting,
        ready: r.ready,
      } satisfies ScoreLineVM;
    })
    .sort((a, b) => a.flowlineId - b.flowlineId);

  const isPending = states.isPending || targets.isPending || done.isPending;
  if (isPending && lines.length === 0) {
    return { data: null, isPending, states: [], workcentres: [], flowlines: [] };
  }

  const totalDone = lines.reduce((sum, l) => sum + l.done, 0);
  // A roll-up only counts the lines that have a target. Summing a known 8 with
  // an unknown would quietly report the unknown as zero.
  const withTarget = lines.filter((l) => l.target != null);
  const totalTarget = withTarget.length ? withTarget.reduce((sum, l) => sum + (l.target ?? 0), 0) : null;
  const totalDue = dueByNow(totalTarget, window);

  return {
    data: {
      lines,
      done: totalDone,
      target: totalTarget,
      dueNow: totalDue,
      delta: totalDue == null ? null : totalDone - totalDue,
      onShift: window.onShift,
    },
    isPending,
    states: states.data ?? [],
    workcentres: workcentres.data ?? [],
    flowlines: flowlines.data ?? [],
  };
}

/**
 * The scope chips: which flowlines this work centre is running today.
 *
 * From the state table rather than the target rows, because a target says what
 * a line is asked to build and not which stations are on it — every station
 * inherits its flowline's number, so targets cannot tell the lines apart.
 */
export function useScopeFlowlines(): Array<{ id: number; name: string; short: string }> {
  const wc = useStore((s) => s.workcentreId);
  const states = useStationStates();
  const flowlines = useFlowlines();
  return useMemo(() => {
    const seen = new Map<number, { id: number; name: string; short: string }>();
    for (const r of states.data ?? []) {
      if (r.workcentreId !== wc || r.status === 'no_work' || seen.has(r.flowlineId)) continue;
      seen.set(r.flowlineId, {
        id: r.flowlineId,
        name: r.flowlineName,
        short: shortFlowline(r.flowlineName, flowlines.data ?? []),
      });
    }
    return [...seen.values()].sort((a, b) => a.id - b.id);
  }, [wc, states.data, flowlines.data]);
}

/** The flowlines in scope right now — one when a chip is picked, all otherwise. */
export function useScopeFlowlineIds(): number[] {
  const fl = useStore((s) => s.flowlineId);
  const all = useScopeFlowlines();
  return useMemo(() => (fl == null ? all.map((f) => f.id) : [fl]), [fl, all]);
}

/**
 * The leader's scope chips: every flowline on the floor.
 *
 * Not the station's lines — a leader on the floor is not standing anywhere,
 * so the filter is over the whole plant. The same store field holds the pick
 * as the operator's, so drilling into a station keeps the line in view.
 */
export function useFloorScopeFlowlines(): Array<{ id: number; name: string; short: string }> {
  const flowlines = useFlowlines();
  return useMemo(
    () =>
      (flowlines.data ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        short: shortFlowline(f.name, flowlines.data ?? []),
      })),
    [flowlines.data],
  );
}

export function useFloorScopeFlowlineIds(): number[] {
  const fl = useStore((s) => s.flowlineId);
  const all = useFloorScopeFlowlines();
  return useMemo(() => (fl == null ? all.map((f) => f.id) : [fl]), [fl, all]);
}

/** "FL 3" for a flowline id, or its name when it has no prefix. */
export function useFlowlineShort(): (flowlineId: number | null) => string {
  const flowlines = useFlowlines();
  return useMemo(() => {
    const list = flowlines.data ?? [];
    return (id: number | null) => {
      const f = list.find((x) => x.id === id);
      return f ? shortFlowline(f.name, list) : '';
    };
  }, [flowlines.data]);
}

/**
 * The End-of-Line QC work centre: where a pod counts for the floor.
 *
 * From `mrp.workcenter.is_eol`, never an id. Where more than one is flagged
 * the first by sequence answers, which is the one the floor means.
 */
export function useEolWorkcentre(): WorkcentreVM | null {
  const workcentres = useWorkcentres();
  return useMemo(
    () => [...(workcentres.data ?? [])].filter((w) => w.isEol).sort((a, b) => a.sequence - b.sequence)[0] ?? null,
    [workcentres.data],
  );
}

/**
 * "FL 3" — the chip label.
 *
 * From the flowline's own prefix where there is one ("FL-3" → "FL 3"), because
 * that is what is written on the line itself. The name is the fallback.
 */
function shortFlowline(name: string, flowlines: FlowlineVM[]): string {
  const match = flowlines.find((f) => f.name === name);
  if (match?.prefix) return match.prefix.replace(/^FL-?/i, 'FL ').replace(/\s+/g, ' ').trim();
  return name.replace(/^Flowline\s*/i, 'FL ').trim();
}

// ── leader ─────────────────────────────────────────────────────────────────

/**
 * How far a station's own target may exceed its line's plan before it stops
 * being a pod count.
 *
 * GRC Cure carries a target of 6000 against a line planning twelve pods — it is
 * counting components, not pods, and adding it to an area's score would drown
 * every other station in it. There is no unit on the target model to ask, so
 * the ratio is the signal: a station running to its line's plan, or to a small
 * multiple of it, is counting the same things the line is.
 *
 * Such a station is not hidden. It keeps its own tile and its own number; it
 * simply does not join a sum it would not mean the same thing in.
 */
const POD_UNIT_MULTIPLE = 5;

export function useDepartments() {
  return useQuery({ queryKey: keys.departments, queryFn: api.departments, staleTime: 30 * MINUTE });
}

/**
 * The leader board: every work centre in scope, with its score and its state.
 *
 * Scope is a leaf department, or all of them. Each station is measured against
 * its own flowlines' targets and its own shift calendar, then the ones counting
 * pods are summed for the headline — see POD_UNIT_MULTIPLE for why the others
 * stand apart.
 */
/**
 * One row per work centre PER FLOWLINE across the floor, with its score, its
 * state and the word for it.
 *
 * This is the table every leader view is drawn from: the Work centres view
 * groups it by department, the Overview counts its exceptions per line and
 * lists the starved ones, the Alerts tab lists them again in full. A station on
 * three lines is three pairs, and they are different numbers — the tile in the
 * Flowline 1 column must say what that station did on Flowline 1.
 *
 * Each pair is measured against its own flowline's target and its own shift
 * calendar; `countsTowardScore` marks the ones counting pods rather than
 * components (see POD_UNIT_MULTIPLE), for the sums that must not mix them.
 */
export interface FloorPair {
  state: StationStateVM;
  workcentre: WorkcentreVM;
  done: number;
  target: number | null;
  dueNow: number | null;
  tone: StationTone;
  countsTowardScore: boolean;
}

export function useFloorPairs(flowlineIds: number[]): { pairs: FloorPair[]; isPending: boolean } {
  const states = useStationStates();
  const targets = useTargets();
  const done = useDoneToday(null);
  const workcentres = useWorkcentres();
  const calendars = useCalendars((workcentres.data ?? []).map((w) => w.calendarId));

  const isPending = states.isPending || targets.isPending || done.isPending || workcentres.isPending;
  const pairs = useMemo(() => {
    const wcById = new Map((workcentres.data ?? []).map((w) => [w.id, w]));
    const out: FloorPair[] = [];
    for (const r of states.data ?? []) {
      const wc = wcById.get(r.workcentreId);
      if (!wc || !flowlineIds.includes(r.flowlineId)) continue;
      const inherited = targets.data?.flowline[r.flowlineId]?.target ?? null;
      const override = targets.data?.workcentre[`${wc.id}:${r.flowlineId}`];
      const target = override ?? inherited ?? (r.targetToday || null);
      const window = paceWindow(calendars.data?.[wc.calendarId ?? -1] ?? null);
      const dueNow = dueByNow(target, window);
      const count = (done.data ?? [])
        .filter((d) => d.workcentreId === wc.id && d.flowlineId === r.flowlineId)
        .reduce((sum, d) => sum + d.count, 0);
      out.push({
        state: r,
        workcentre: wc,
        done: count,
        target,
        dueNow,
        tone: stationTone(r, count, target, dueNow),
        countsTowardScore: !(target != null && inherited != null && target > inherited * POD_UNIT_MULTIPLE),
      });
    }
    return out;
  }, [states.data, targets.data, done.data, workcentres.data, calendars.data, flowlineIds]);

  return { pairs, isPending };
}

// ── needs you ──────────────────────────────────────────────────────────────

/**
 * Open andons this operator raised, plus any at their station.
 *
 * Both, because they answer different halves of the same question: "did anyone
 * come about the one I called in" and "is this station stopped". Read only —
 * acknowledging happens in the Andon app.
 */
export function useMyAndons(enabled = true) {
  const employeeId = useStore((s) => s.operator?.id ?? null);
  return useQuery({
    queryKey: keys.andons(`mine:${employeeId}`),
    queryFn: () => api.andons({ employeeId: employeeId! }),
    enabled: enabled && employeeId != null,
    refetchInterval: 30_000,
  });
}

export function useStationAndons(enabled = true) {
  const workcentreId = useStore((s) => s.workcentreId);
  return useQuery({
    queryKey: keys.andons(`wc:${workcentreId}`),
    queryFn: () => api.andons({ workcentreId: workcentreId! }),
    enabled: enabled && workcentreId != null,
    refetchInterval: 30_000,
  });
}

/** Every unacknowledged andon on the floor — the leader's first section. */
export function useUnacknowledgedAndons(enabled = true) {
  return useQuery({
    queryKey: keys.andons('unack'),
    queryFn: () => api.andons({ unacknowledged: true, limit: 25 }),
    enabled,
    refetchInterval: 20_000,
  });
}

/** Open andons this leader owns. */
export function useMyOwnedAndons(enabled = true) {
  const uid = useStore((s) => s.userUid);
  return useQuery({
    queryKey: keys.andons(`owner:${uid}`),
    queryFn: () => api.andons({ ownerUid: uid! }),
    enabled: enabled && uid != null,
    refetchInterval: 60_000,
  });
}

/**
 * The Odoo user behind the signed-in operator, when they have one.
 *
 * A credentials session already knows its own uid. A PIN session knows only an
 * employee, and most floor employees have no user at all — so this asks once
 * and remembers the answer, including when the answer is "none".
 */
export function useOperatorUserId(): number | null {
  const uid = useStore((s) => s.userUid);
  const employeeId = useStore((s) => s.operator?.id ?? null);
  const looked = useQuery({
    queryKey: ['operatorUser', employeeId],
    queryFn: () => api.userIdForEmployee(employeeId!),
    enabled: uid == null && employeeId != null,
    staleTime: 30 * MINUTE,
  });
  return uid ?? looked.data ?? null;
}

export function useTodos(enabled = true) {
  const uid = useOperatorUserId();
  return useQuery({
    queryKey: keys.todos(uid),
    queryFn: () => api.todos(uid!),
    enabled: enabled && uid != null,
    refetchInterval: 5 * MINUTE,
  });
}

/** Andons assigned to this person — owner_id is their Odoo user, when they have one. */
export function useAssignedAndons(enabled = true) {
  const uid = useOperatorUserId();
  return useQuery({
    queryKey: keys.andons(`assigned:${uid}`),
    queryFn: () => api.andons({ ownerUid: uid! }),
    enabled: enabled && uid != null,
    refetchInterval: 30_000,
  });
}

/** Open quality alerts whose Responsible is this person's Odoo user. */
export function useAssignedAlerts(enabled = true) {
  const uid = useOperatorUserId();
  return useQuery({
    queryKey: keys.alerts(`assigned:${uid}`),
    queryFn: () => api.alerts({ userId: uid! }),
    enabled: enabled && uid != null,
    refetchInterval: 60_000,
  });
}

/**
 * "Needs you" — andons, quality alerts and todos as one list.
 *
 * Three different records with the same job on this screen: something is
 * waiting on this person. Flattening them here rather than in the component
 * means the phone card, the tablet rail and the Alerts tab all order them the
 * same way, worst first.
 *
 * "This person" is meant literally: raised by them, or assigned to them. It
 * used to include everything stamped with their work centre as well, which
 * put a colleague's andon and the previous shift's quality alert on an
 * operator's badge as if they were theirs to answer. The station's open
 * records are still a tap away on the pod and, for a leader, on the floor.
 */
export function useNeedsYou(enabled = true): {
  items: NeedsYouVM[];
  isPending: boolean;
} {
  const t = useT();
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const mine = useMyAndons(enabled);
  const owned = useAssignedAndons(enabled);
  const alerts = useMyAlerts(enabled);
  const assigned = useAssignedAlerts(enabled);
  const todos = useTodos(enabled);

  const items: NeedsYouVM[] = [];

  // De-duplicated: an andon I raised and then took on is one row, not two.
  const seen = new Set<number>();
  for (const a of [...(mine.data ?? []), ...(owned.data ?? [])]) {
    if (seen.has(a.id)) continue;
    seen.add(a.id);
    items.push({
      key: `andon:${a.id}`,
      kind: 'andon',
      ref: a.ref,
      title: a.title,
      meta: andonMeta(a, t),
      tone: a.stars >= 3 || a.overdue ? 'danger' : 'warn',
      // A link out only while the Andon app owns the thread. With the
      // preview on the row opens this app's own, and must not say it leaves.
      ...(andonPreview ? {} : { href: andonHref(andonAppUrl, a.id) }),
      andon: a,
    });
  }

  // De-duplicated the same way: an alert I raised and am responsible for is one row.
  const seenQa = new Set<number>();
  for (const q of [...(alerts.data ?? []), ...(assigned.data ?? [])]) {
    if (seenQa.has(q.id)) continue;
    seenQa.add(q.id);
    items.push({
      key: `quality:${q.id}`,
      kind: 'quality',
      ref: q.qa,
      title: q.title,
      meta: [q.mo, q.team, q.age, q.preventMoSignoff ? t.blocksSignoffT : null].filter(Boolean).join(' · '),
      tone: 'warn',
      alert: q,
    });
  }

  // Named `todo` rather than `t`, which is the translations object here.
  for (const todo of todos.data ?? []) {
    items.push({
      key: `todo:${todo.id}`,
      kind: 'todo',
      ref: '',
      title: todo.summary,
      meta: [todo.about, todo.state === 'overdue' ? t.overdueT : todo.deadline ? dueWord(todo.deadline, t) : null]
        .filter(Boolean)
        .join(' · '),
      tone: todo.state === 'overdue' ? 'warn' : 'neutral',
      todo,
    });
  }

  const rank: Record<NeedsYouVM['tone'], number> = { danger: 0, warn: 1, neutral: 2 };
  items.sort((a, b) => rank[a.tone] - rank[b.tone]);

  return {
    items,
    // Only the reads that always run: the assigned ones are disabled for the
    // many floor employees with no Odoo user, and a disabled query is pending
    // forever.
    isPending: mine.isPending || alerts.isPending,
  };
}

/** The one meta line under an andon: who is on it, and whether they are late. */
function andonMeta(a: AndonVM, t: Translations): string {
  const parts: string[] = [];
  if (a.respondingAt) parts.push(`${a.ownerName || '—'} ${t.respondingT}`);
  else if (a.ownerName) parts.push(`${a.ownerName} ${t.onItT}`);
  else parts.push(t.nobodyYetT);
  if (a.etaMinutes) parts.push(`ETA ${a.etaMinutes} ${t.minT}`);
  if (a.overdue) parts.push(t.slaMissedT);
  else parts.push(`${a.ageMinutes} ${t.minT}`);
  return parts.join(' · ');
}

/** "today" / "overdue" / a date — never a raw ISO string on a shop floor. */
function dueWord(deadline: string, t: Translations): string {
  const today = localDateKey();
  if (deadline === today) return t.dueTodayT;
  return deadline < today ? t.overdueT : `${t.dueT} ${deadline}`;
}

/**
 * Where an andon opens.
 *
 * The Andon app when the deployment names one, and the Odoo record otherwise —
 * so the link always goes somewhere the alert can actually be acknowledged,
 * rather than being a dead row until an env var is set.
 */
export function andonHref(andonAppUrl: string | null, id: number): string {
  return andonAppUrl
    ? `${andonAppUrl}/alert/${id}`
    : `/odoo/action-base.action_ui_view?model=andon.alert&id=${id}&view_type=form`;
}

/** Everyone on the clock across the floor — the leader board's head count. */
export function useCrewOnFloor() {
  return useQuery({
    queryKey: ['crew', 'floor'],
    queryFn: api.crewOnFloor,
    refetchInterval: 2 * MINUTE,
    staleTime: MINUTE,
  });
}

/** How many andons are open across the whole floor — the Alerts tab's footnote. */
export function useAndonOpenCount() {
  return useQuery({
    queryKey: keys.andons('openCount'),
    queryFn: api.andonOpenCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
}

export function usePodAlerts(moId: number | null) {
  return useQuery({
    queryKey: keys.alerts(`mo:${moId}`),
    queryFn: () => api.alerts({ moId: moId! }),
    enabled: moId != null,
    refetchInterval: 30_000,
  });
}

/**
 * Open alerts this operator raised, anywhere — not just on the pod in front of
 * them. Raising one ends with "track it in my quality alerts", and this is what
 * that promise refers to.
 */
export function useMyAlerts(enabled = true) {
  const employeeId = useStore((s) => s.operator?.id ?? null);
  return useQuery({
    queryKey: keys.alerts(`mine:${employeeId}`),
    queryFn: () => api.alerts({ mine: true, employeeId: employeeId! }),
    enabled: enabled && employeeId != null,
    refetchInterval: 60_000,
  });
}

/**
 * Open quality alerts stamped with this station.
 *
 * Most rows in Odoo have no work centre on them at all — raised from contexts
 * that did not know the station — so this is honestly short. The ones this
 * app raises are stamped, which is what will fill it.
 */
export function useStationAlerts(enabled = true) {
  const workcentreId = useStore((s) => s.workcentreId);
  return useQuery({
    queryKey: keys.alerts(`wc:${workcentreId}`),
    queryFn: () => api.alerts({ workcentreId: workcentreId! }),
    enabled: enabled && workcentreId != null,
    refetchInterval: 60_000,
  });
}

/**
 * Open quality alerts across the floor, for a leader.
 *
 * Minus the excluded teams (see SF_QA_EXCLUDED_TEAM_IDS) — thousands of room
 * site-works rows would otherwise bury the handful about the line. `blocking`
 * narrows to the ones holding a pod's sign-off, which is what the Overview's
 * "needs you" list shows; the Alerts tab shows them all.
 */
export function useFloorAlerts(enabled = true, blocking = false) {
  const excluded = useStore((s) => s.qaExcludedTeamIds);
  return useQuery({
    queryKey: keys.alerts(`floor:${blocking ? 'blocking' : 'all'}:${excluded.join('.')}`),
    queryFn: () => api.alerts({ excludeTeamIds: excluded, blocking, limit: 50 }),
    enabled,
    refetchInterval: 60_000,
  });
}

/**
 * One quality alert, for the sheet — read fresh, whatever list it was opened
 * from. With the andon preview on it carries the andons it is linked to; see
 * `ALERT_FIELDS_WITH_ANDONS` for why that is a switch and not the default.
 */
export function useAlert(alertId: number | null) {
  const withAndons = useStore((s) => s.andonCaps.preview);
  return useQuery({
    queryKey: keys.alerts(`one:${alertId}:${withAndons ? 'andons' : 'plain'}`),
    queryFn: () => api.alertById(alertId!, withAndons),
    enabled: alertId != null,
    refetchInterval: 30_000,
  });
}

/** One andon, for the detail sheet. */
export function useAndon(andonId: number | null) {
  return useQuery({
    queryKey: keys.andons(`one:${andonId}`),
    queryFn: () => api.andonById(andonId!),
    enabled: andonId != null,
    refetchInterval: 30_000,
  });
}

/**
 * The andons a quality alert carries, by id, open or not.
 *
 * For the quality sheet's linked-andon row. A closed andon is still shown —
 * the sheet is read on an alert that outlives it, and "the andon that raised
 * this is closed" is part of the alert's story.
 */
export function useAndonsByIds(ids: number[]) {
  const key = [...ids].sort((a, b) => a - b).join(',');
  return useQuery({
    queryKey: keys.andons(`ids:${key}`),
    queryFn: () => api.andons({ ids, limit: ids.length }),
    enabled: ids.length > 0,
    refetchInterval: 30_000,
  });
}

/** The stages a team's alerts move between. */
export function useQualityStages(teamId: number | null) {
  return useQuery({
    queryKey: keys.qualityStages(teamId),
    queryFn: () => api.qualityStages(teamId!),
    enabled: teamId != null,
    staleTime: 30 * MINUTE,
  });
}

/** What has been said on one alert. */
export function useAlertMessages(alertId: number | null, enabled = true) {
  return useQuery({
    queryKey: keys.alertMessages(alertId),
    queryFn: () => api.alertMessages(alertId!),
    enabled: enabled && alertId != null,
    refetchInterval: 30_000,
  });
}

/**
 * The quality sheet's four writes. Each settles by re-reading the alert and
 * everything listing it; a stage change moves it between lists.
 */
export function useAlertActions(alertId: number | null) {
  const qc = useQueryClient();
  const onError = useWriteError();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['alerts'] });
    qc.invalidateQueries({ queryKey: keys.alertMessages(alertId) });
  };
  const setStage = useMutation({
    mutationFn: (stageId: number) => api.setAlertStage(alertId!, stageId),
    onSuccess: invalidate,
    onError,
  });
  const setCategory = useMutation({
    mutationFn: (v: { categoryId: number | null; subcategoryId: number }) =>
      api.setAlertCategory(alertId!, v.categoryId, v.subcategoryId),
    onSuccess: invalidate,
    onError,
  });
  const post = useMutation({
    mutationFn: (body: string) => api.postAlertMessage(alertId!, body),
    onSuccess: invalidate,
    onError,
  });
  const attach = useMutation({
    mutationFn: (v: { name: string; base64: string; bucket?: PictureBucket }) =>
      api.attachAlertPhoto(v.name, v.base64, alertId!, v.bucket ?? 'other'),
    onSuccess: invalidate,
    onError,
  });
  const movePicture = useMutation({
    mutationFn: (v: { attachmentId: number; bucket: PictureBucket }) => api.movePicture(alertId!, v.attachmentId, v.bucket),
    onSuccess: invalidate,
    onError,
  });
  return { setStage, setCategory, post, attach, movePicture };
}

/** Today's shifts, from attendance: on now, and finished. */
export function useCrewToday(enabled = true) {
  return useQuery({
    queryKey: keys.crewToday,
    queryFn: api.crewToday,
    enabled,
    refetchInterval: 2 * MINUTE,
    staleTime: MINUTE,
  });
}

/** Surface a failed write honestly — nothing was recorded. */
export function useWriteError() {
  const showError = useStore((s) => s.showError);
  return (e: unknown) => {
    const msg =
      e instanceof ApiError
        ? e.kind === 'business'
          ? e.message // Odoo's own operator-facing rule, worth showing verbatim
          : e.kind === 'needOperator'
            ? 'Confirm your PIN to continue.'
            : e.message
        : 'Something went wrong.';
    showError(msg);
    if (e instanceof ApiError && e.kind === 'needOperator') {
      useStore.getState().setReauthRequired(true);
    }
  };
}

/** Work-order lifecycle. Each settles by refetching what the server now says. */
export function useWoLifecycle(woId: number | null, moId: number | null) {
  const qc = useQueryClient();
  const onError = useWriteError();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: keys.woLive(woId) });
    qc.invalidateQueries({ queryKey: keys.checks(woId) });
    qc.invalidateQueries({ queryKey: ['queue'] });
    if (moId) qc.invalidateQueries({ queryKey: keys.journey(moId) });
  };

  const start = useMutation({ mutationFn: () => api.startWo(woId!), onSuccess: invalidate, onError });
  const pause = useMutation({
    mutationFn: async (reason?: string) => {
      await api.pauseWo(woId!);
      // Work orders have no chatter, so the reason goes to the MO.
      if (reason && moId) {
        const who = useStore.getState().operator?.name ?? 'Operator';
        await api.postMoNote(moId, `Paused at ${useStore.getState().workcentreName ?? 'station'} — ${reason} (${who})`).catch(() => {});
      }
    },
    onSuccess: invalidate,
    onError,
  });
  const finish = useMutation({
    mutationFn: async (attestation?: { reason: string; claimedStart?: string }) => {
      if (attestation && moId) {
        const who = useStore.getState().operator?.name ?? 'Operator';
        const when = attestation.claimedStart ? ` Actual start: ${attestation.claimedStart}.` : '';
        await api
          .postMoNote(moId, `Completed faster than expected — ${attestation.reason}.${when} (${who})`)
          .catch(() => {});
      }
      await api.finishWo(woId!);
    },
    onSuccess: invalidate,
    onError,
  });

  return { start, pause, finish };
}

export type CheckAction =
  | { kind: 'pass' }
  | { kind: 'fail' }
  | { kind: 'na' }
  | { kind: 'undo' }
  | { kind: 'measure'; value: number }
  | { kind: 'photo'; base64: string }
  | { kind: 'select'; optionId: number }
  | { kind: 'mould'; mouldId: number }
  | { kind: 'value'; text: string };

/**
 * Check sign-off. Deliberately not optimistic: a sign-off only counts when
 * Odoo confirms it, so the card stays pending until the refetch lands.
 */
export function useCheckAction(woId: number | null) {
  const qc = useQueryClient();
  const onError = useWriteError();
  return useMutation({
    mutationFn: async ({ check, action }: { check: CheckVM; action: CheckAction }) => {
      switch (action.kind) {
        case 'pass':
          return api.passCheck(check.id);
        case 'fail':
          return api.failCheck(check.id);
        case 'na':
          return api.naCheck(check.id);
        case 'undo':
          return api.undoCheck(check.id);
        case 'measure':
          return api.measureCheck(check.id, action.value);
        case 'photo':
          return api.photoCheck(check.id, action.base64);
        case 'select':
          return api.selectOption(check.id, action.optionId);
        case 'mould':
          return api.registerMould(check.id, action.mouldId);
        case 'value':
          return api.registerValue(check.id, action.text);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.checks(woId) }),
    onError,
  });
}

// The job's Done gate lives in lib/checks so it can be tested on its own;
// re-exported here because every screen reaches it through the queries.
export { checksResolved, type ChecksProgress } from '@/lib/checks';

export function usePod(): { pod: PodVM | undefined; queue: PodVM[] } {
  const queue = useQueue().data ?? [];
  const index = useStore((s) => s.activeIndex);
  return { pod: queue[Math.min(index, Math.max(0, queue.length - 1))], queue };
}


// ── the standard-work library ────────────────────────────────────────────────
//
// Both are read from another app through this one's BFF, and both are far
// slower-moving than anything from Odoo: an ISW changes when a tech. author
// edits and re-renders it, which is weeks apart, not seconds. So neither
// polls. The register is fetched when the library screen opens and kept for
// the session; a record is fetched when a card is opened.

/** Every ISW in the library. Fetched on demand — pass false while unused. */
export function useIswRegister(enabled = true) {
  return useQuery({
    queryKey: keys.iswRegister,
    queryFn: () => api.iswRegister(),
    enabled,
    // The library is a separate deploy; a floor tablet should not sit
    // re-asking it. An operator who needs it fresh reopens the screen.
    staleTime: 15 * MINUTE,
    refetchOnWindowFocus: false,
  });
}

/**
 * One record, in one card language.
 *
 * Keyed on the language because the library resolves a card per language and
 * may answer with a different one than was asked for — an ISW with no Filipino
 * render falls back to English, and the record says which it actually gave.
 */
export function useIswRecord(id: string | null, lang: IswLang) {
  return useQuery({
    queryKey: keys.iswRecord(id ?? '', lang),
    queryFn: () => api.iswRecord(id as string, lang),
    enabled: !!id,
    staleTime: 15 * MINUTE,
    refetchOnWindowFocus: false,
  });
}


// ── the Plans & documents sheet ──────────────────────────────────────────────

/**
 * One folder of a pod's controlled drawings.
 *
 * No staleTime worth the name, on purpose: Odoo reads every revision fresh
 * from SharePoint precisely so that nobody builds to a superseded sheet, and a
 * listing cached here for fifteen minutes would undo that. It is read when the
 * sheet opens and not polled — the file route itself redirects a stale
 * revision to the current one, so a listing left open cannot open old bytes.
 */
export function useDrawings(moId: number | null, folderId: string | null) {
  return useQuery({
    queryKey: keys.drawings(moId, folderId),
    queryFn: () => api.drawings(moId as number, folderId),
    enabled: moId != null && moId > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // A missing binding is an answer, not a failure (it comes back as
    // `unavailable`), so an error here is Odoo or SharePoint being down.
    // Once is enough to ask again before saying so.
    retry: 1,
  });
}
